import { KeyboardArrowRight } from "@mui/icons-material";
import { ChevronDown, ChevronUp } from "lucide-react";
import React, { useState, useEffect, useMemo } from "react";
import AudioPlayerForRouting from "../reusables/AudioPlayerForRouting";
import TooltipComponent from "../reusables/TooltipComponent";
import RouteSelect from "../reusables/RouteSelect";
import { formatPhoneNumber } from "../utils/formatnumber";
import { Divider } from "@mui/material";

const DEFAULT_CALL_APPROVAL = "disabled";

/**
 * Pure UI component for ForwardVM action
 * All logic is handled by useForwardVMAction hook
 */
const ForwardVMUI = ({
  numberValue,
  callerIdPref,
  callApproval,
  recordingValue,
  handleNumberChange,
  handleCallerIdChange,
  handleCallApprovalChange,
  handleRecordingChange,
  containerClass,
  inputClass,
  headerSize,
  showDivider,
  callerIdOptions,
  setOpenConfigureSlotModal,
  channels = [],
  recordings = [],
  hasValidationError = false,
  addDestinationOption,
  addRecordingOption,
  /** When provided, used for destination select so primaryNumber/offHoursNumber store phone, not channel_id */
  onDestinationChange,
}) => {
  const defaultCallerId = callerIdOptions?.[0]?.value ?? "original";
  const isNonDefaultCallerId = callerIdPref !== defaultCallerId && callerIdPref !== "";
  const hasNonDefaultAdvanced = isNonDefaultCallerId || callApproval !== DEFAULT_CALL_APPROVAL;
  const [advancedOpen, setAdvancedOpen] = useState(() => hasNonDefaultAdvanced);

  useEffect(() => {
    if (hasNonDefaultAdvanced) {
      setAdvancedOpen(true);
    }
  }, [hasNonDefaultAdvanced]);

  const destinationOptions = useMemo(() => {
    if (!channels) return [];
    return channels
      .filter((item) => item.M.type.S === "forward_number")
      .map((item) => {
        const chId = item.M.ch_id?.S ?? item.ch_id ?? item.M.value?.S;
        const formattedNumber = formatPhoneNumber(item.M.value?.S ?? item.M.value ?? "");
        const label = item.M.label?.S ?? item.label ?? "";
        return { value: chId, label: label ? `${label} - ${formattedNumber}` : formattedNumber };
      });
  }, [channels]);

  const destinationValue = useMemo(() => {
    if (!numberValue) return numberValue;
    const hasMatch = destinationOptions.some((o) => o.value === numberValue);
    if (hasMatch) return numberValue;
    const byPhone = channels?.find(
      (c) => c.M?.type?.S === "forward_number" && (c.M.value?.S ?? c.value) === numberValue
    );
    if (byPhone) return byPhone.M?.ch_id?.S ?? byPhone.ch_id ?? numberValue;
    return numberValue;
  }, [numberValue, destinationOptions, channels]);

  const recordingOptions = useMemo(() => {
    if (!recordings) return [];
    return recordings.map((item) => ({ value: item.M.rec_id.S, label: item.M.title.S }));
  }, [recordings]);

  // Find the recording URL for audio player
  const recordingUrl = recordingValue
    ? recordings?.find((e) => e.M.rec_id.S === recordingValue)?.M.url.S
    : null;

  return (
    <div>
      <div className={`${containerClass}${hasValidationError ? " !bg-red-50/80" : ""}`}>
        {/* Header */}
        <div className="flex flex-nowrap items-center justify-between gap-3 mb-3">
          <div className="flex items-center min-w-0 flex-1">
            <KeyboardArrowRight className="text-green-500 shrink-0" />
            <div className="min-w-0 ml-2">
              <h2 className={`${headerSize} text-gray-800`}>
                Forward Call w/VM
              </h2>
              <div className="text-[13px]">
                Forward with voicemail fallback
              </div>
            </div>
          </div>
          {setOpenConfigureSlotModal && (
            <button
              className="shrink-0 whitespace-nowrap text-sm text-blue-600 font-medium hover:underline text-[13px]"
              onClick={() => setOpenConfigureSlotModal(true)}
            >
              ↻ Change
            </button>
          )}
        </div>
        {showDivider && <Divider />}

        {/* Destination */}
        <div className="mb-4">
          <div className="flex items-center gap-1 mb-1">
            <span className="text-sm font-medium text-gray-700">
              Destination
            </span>
            <TooltipComponent message={"Select the number to forward the call to"} />
          </div>
          <RouteSelect
            value={destinationValue}
            onChange={onDestinationChange ?? handleNumberChange}
            options={destinationOptions}
            placeholder="Select a destination"
            addOption={addDestinationOption}
          />
        </div>

        {/* Recording */}
        <div className="mb-4">
          <div className="flex items-center gap-1 mb-1">
            <span className="text-sm font-medium text-gray-700">Recording</span>
            <TooltipComponent message={"Select the voicemail message to play if the call isn't answered"} />
          </div>
          <RouteSelect
            value={recordingValue}
            onChange={handleRecordingChange}
            options={recordingOptions}
            placeholder="Select a recording"
            addOption={addRecordingOption}
          />
        </div>

        {recordingUrl && (
          <AudioPlayerForRouting src={recordingUrl} />
        )}

        {/* Advanced Settings (collapsible) */}
        <div>
          <button
            type="button"
            onClick={() => setAdvancedOpen((prev) => !prev)}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            {advancedOpen ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            <span>Advanced Settings</span>
          </button>
          {advancedOpen && (
            <div className="mt-3 space-y-4">
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-sm font-medium text-gray-700">
                    Caller ID Setting
                  </span>
                  <TooltipComponent message={"Choose which caller ID to see when the call is forwarded"} />
                </div>
                <RouteSelect
                  value={callerIdPref}
                  onChange={handleCallerIdChange}
                  options={callerIdOptions}
                  placeholder={showDivider ? "select preference" : "Select a preference"}
                />
              </div>
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-sm font-medium text-gray-700">
                    Call Approval
                  </span>
                  <TooltipComponent message={"When enabled, you'll hear who is calling and can press 1 to connect or let it go to voicemail"} />
                </div>
                <RouteSelect
                  value={callApproval}
                  onChange={handleCallApprovalChange}
                  options={[
                    { value: "disabled", label: "Disabled - Connect Immediately" },
                    { value: "enabled", label: "Enabled - Press 1 to connect" },
                  ]}
                  placeholder="Select"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForwardVMUI;
